import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';

import { useAuthStore } from './stores/authStore';
import ProtectedRoute from './components/ProtectedRoute';
import MainLayout from './layouts/MainLayout';

// Lazy load pages for better performance
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const MFASetupPage = lazy(() => import('./pages/auth/MFASetupPage'));
const ChangePasswordPage = lazy(() => import('./pages/auth/ChangePasswordPage'));

const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const AssetsPage = lazy(() => import('./pages/assets/AssetsPage'));
const AssetDetailPage = lazy(() => import('./pages/assets/AssetDetailPage'));
const AssetFormPage = lazy(() => import('./pages/assets/AssetFormPage'));
const UsersPage = lazy(() => import('./pages/users/UsersPage'));
const LocationsPage = lazy(() => import('./pages/locations/LocationsPage'));
const TransfersPage = lazy(() => import('./pages/transfers/TransfersPage'));
const AuditLogPage = lazy(() => import('./pages/audit/AuditLogPage'));
const AssetAuditPage = lazy(() => import('./pages/audit/AssetAuditPage'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const BulkUploadPage = lazy(() => import('./pages/bulk-upload/BulkUploadPage'));
const QRLabelPage = lazy(() => import('./pages/qr/QRLabelPage'));
const RolesPage = lazy(() => import('./pages/roles/RolesPage'));
const LabelPrintQueuePage = lazy(() => import('./pages/labels/LabelPrintQueuePage'));
const MasterDataPage = lazy(() => import('./pages/masters/MasterDataPage'));

// Loading fallback - Lucas TVS Green branding
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      width: '100%',
      background: 'linear-gradient(135deg, #00843D 0%, #00A94F 100%)',
    }}
  >
    <Box
      sx={{
        width: 80,
        height: 80,
        borderRadius: 3,
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 3,
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      }}
    >
      <Box
        component="span"
        sx={{
          fontFamily: 'Arial Black, sans-serif',
          fontWeight: 900,
          fontSize: '28px',
          color: '#00843D',
          fontStyle: 'italic',
        }}
      >
        LT
      </Box>
    </Box>
    <CircularProgress size={40} sx={{ color: '#fff' }} />
    <Box
      component="span"
      sx={{
        mt: 2,
        color: '#fff',
        fontSize: '14px',
        fontWeight: 500,
        letterSpacing: '0.1em',
      }}
    >
      Loading...
    </Box>
  </Box>
);

function App() {
  const { isAuthenticated } = useAuthStore();

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
          }
        />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          
          {/* Assets */}
          <Route path="/assets" element={<AssetsPage />} />
          <Route path="/assets/new" element={<AssetFormPage />} />
          <Route path="/assets/:id" element={<AssetDetailPage />} />
          <Route path="/assets/:id/edit" element={<AssetFormPage />} />
          
          {/* Users */}
          <Route path="/users" element={<UsersPage />} />
          
          {/* Roles */}
          <Route path="/roles" element={<RolesPage />} />
          
          {/* Locations */}
          <Route path="/locations" element={<LocationsPage />} />
          
          {/* Transfers */}
          <Route path="/transfers" element={<TransfersPage />} />
          
          {/* Asset Audit - QR Scanning */}
          <Route path="/asset-audit" element={<AssetAuditPage />} />
          
          {/* Audit Logs */}
          <Route path="/audit-logs" element={<AuditLogPage />} />
          
          {/* Reports */}
          <Route path="/reports" element={<ReportsPage />} />
          
          {/* Settings */}
          <Route path="/settings" element={<SettingsPage />} />
          
          {/* Profile */}
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/mfa-setup" element={<MFASetupPage />} />
          
          {/* Bulk Upload */}
          <Route path="/bulk-upload" element={<BulkUploadPage />} />
          
          {/* QR Management - under Assets */}
          <Route path="/qr" element={<QRLabelPage />} />
          
          {/* Label Print Queue */}
          <Route path="/label-printing" element={<LabelPrintQueuePage />} />

          {/* Master Data */}
          <Route path="/master-data" element={<MasterDataPage />} />
        </Route>

        {/* Default redirect */}
        <Route
          path="/"
          element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />}
        />
        
        {/* 404 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;

